import { ReactElement, useRef } from 'react';
import { useSelector } from 'react-redux';
import { SortBy, SortByParam, SortOrder } from '../../models/SortOrder';
import ScrollButton from '../Shared/Buttons/ScrollButton';
import BillViewDropdown from '../Shared/DropDowns/BillViewDropdown';
import SortDropdown from '../Shared/DropDowns/SortDropdown';
import BillList from '../Shared/Lists/BillList';
import Paginator from '../Shared/PageElements/Paginator';
import { getJurisdictionsForTeamMode } from '../../dux';
import { BillSearchParams } from '@enview/interface/types/BillSearch';
import { BillSummary } from '@enview/interface/types/bills/Bill';
import Info from '../../components/svg/InfoCircleIcon';
import { TOO_MANY_RESULTS_THRESHOLD } from '../../helpers/BillSearchHelper';

const LEG_SORT_BILL_OPTIONS = [
  new SortOrder(SortBy.RELEVANCE, false),
  new SortOrder(SortBy.RECENT, false),
  new SortOrder(SortBy.RECENT, true),
  new SortOrder(SortBy.ALPHANUMERICAL, false),
  new SortOrder(SortBy.ALPHANUMERICAL, true),
];

const PAGE_SIZE = 25;

type BillSearchResultProps = {
  params: BillSearchParams;
  searching: boolean;
  query: any;
  setQuery: (arg: any) => void;
  results:
    | {
        data: BillSummary[];
        count: number;
      }
    | undefined;
  isSuccess: boolean;
  onOpenSearchTips?: () => void;
};

const BillSearchResult = (props: BillSearchResultProps): ReactElement => {
  const { searching, query, setQuery, results, isSuccess, onOpenSearchTips } = props;

  const orgJurisdictions = useSelector(getJurisdictionsForTeamMode);

  const defaultOrder = query.keywords
    ? SortOrder.getBillSortOrderFromParam(SortByParam.RELEVANCE)
    : SortOrder.getBillSortOrderFromParam(SortByParam.LATEST);
  const selectedBillOrder = query.order
    ? SortOrder.getBillSortOrderFromParam(query.order)
    : defaultOrder;

  const ref = useRef<HTMLDivElement>(null);

  const handleSortOrderChange = (billSortOrder: SortOrder): void => {
    if (selectedBillOrder && selectedBillOrder.equals(billSortOrder)) return;
    setQuery({ order: billSortOrder.getQueryParam(), page: 1 });
  };

  const renderBillList = (): ReactElement => {
    if (!results) {
      return <></>;
    }
    if (results.count === 0) {
      return (
        <div className="card-result">
          <BillList
            bills={[]}
            isAISearch={!!query.isAISearch}
            onStartingPointClick={(phrase) =>
              setQuery({
                ...query,
                keywords: phrase,
                jurisdictions: orgJurisdictions.map((j) => j.abbreviation),
                page: 1,
              })
            }
            showAIStartingPoint={!!query.isAISearch && !query.keywords}
          />
        </div>
      );
    }
    const pages = { page: query.page, total: results.count, pageSize: PAGE_SIZE };
    return (
      <div className="card-result">
        {ref && ref.current && <ScrollButton div={ref.current} />}
        <BillList bills={results.data} />
        <Paginator onChange={(page) => setQuery({ page })} pages={pages} />
      </div>
    );
  };

  return (
    <>
      <div className="results-pane list">
        {results && results.count > 0 && !searching && isSuccess && (
          <div
            className={`result-count result-count--card${
              query.isAISearch ? ' has-tip' : ''
            }`}
          >
            <div className="result-count-info">
              <div className="result-total">{results.count} Results</div>

              {!!query.isAISearch && results.count >= TOO_MANY_RESULTS_THRESHOLD && (
                <div className="too-many-results-tip">
                  <Info
                    className="too-many-results-tip__icon"
                    color="#f68c24"
                    size={14}
                  />
                  <strong>Too many results?</strong> Add more specificity to your topic
                  for more focused results.{' '}
                  <button
                    className="search-tips-link"
                    onClick={onOpenSearchTips}
                    type="button"
                  >
                    See search tips
                  </button>
                </div>
              )}
            </div>

            <div className="result-count-actions">
              <SortDropdown
                setSortOrder={handleSortOrderChange}
                sortOptions={LEG_SORT_BILL_OPTIONS}
                sortOrder={selectedBillOrder || defaultOrder}
              />

              <div className="view-dropdown">
                <BillViewDropdown />
              </div>
            </div>
          </div>
        )}
        {isSuccess && renderBillList()}
      </div>
    </>
  );
};

export default BillSearchResult;
